import { useRef, useCallback, useEffect, useMemo } from 'react';
import {
  getMarkerIconForState as getMarkerIconForAws,
  MarkerState as AwsMarkerState
} from '@kyruus/aws-map';
import {
  getMarkerIconForState as getMarkerIconForGmap,
  MarkerState as GmapMarkerState,
  ZINDEXES
} from '@kyruus/gmap';

/**
 * Generates a unique hash key for a given location and provider
 * @param {Number} locationId
 * @param {Number} providerId
 * @returns {String} - The generated hash key
 */
const hashKey = (locationId, providerId) => {
  return `${locationId}_${providerId}`;
};

/**
 * Hook to handle provider highlighting on map markers with keyboard navigation support
 * Works with custom marker components to provide dynamic highlighting
 *
 * @param {Array} providers - Array of provider objects
 * @param {string|null} hoveredProviderId - Currently hovered provider ID
 *
 * @returns {Object} - Highlighting functions and marker management
 */
export const useMapHighlighting = (providers, hoveredProviderId) => {
  const markersRef = useRef([]);

  const providerLocationMap = useMemo(() => {
    const map = new Map();
    if (providers && Array.isArray(providers)) {
      providers.forEach((provider) => {
        if (provider.locations) {
          provider.locations.forEach((location) => {
            map.set(hashKey(location.id, provider.id), provider.id);
          });
        }
      });
    }
    return map;
  }, [providers]);

  const highlightGoogleMapsMarkers = useCallback(
    (providerId) => {
      if (!window || !window.google) return;

      markersRef.current.forEach(({ marker, meta }) => {
        const markerProviderId = providerLocationMap.get(
          hashKey(meta.location.id, providerId)
        );

        if (marker && marker.setIcon) {
          const isHighlighted = providerId && markerProviderId === providerId;

          // Use pre-encoded SVG data URLs
          const dataUrl = isHighlighted
            ? getMarkerIconForGmap(GmapMarkerState.HOVERED, {
                count: meta.location.providerCount
              })
            : getMarkerIconForGmap(GmapMarkerState.DEFAULT, {
                count: meta.location.providerCount
              });

          const iconConfig = {
            url: dataUrl
          };

          marker.setIcon(iconConfig);
          marker.setZIndex(isHighlighted ? ZINDEXES.HOVERED : ZINDEXES.DEFAULT);
        }
      });
    },
    [providerLocationMap]
  );

  const highlightAwsMapMarkers = useCallback(
    (providerId) => {
      markersRef.current.forEach(({ marker, meta }) => {
        const markerProviderId = providerLocationMap.get(
          hashKey(meta.location.id, providerId)
        );

        if (marker && marker.getElement) {
          const isHighlighted = providerId && markerProviderId === providerId;
          const markerElement = marker.getElement();

          if (markerElement) {
            const dataUrl = isHighlighted
              ? getMarkerIconForAws(AwsMarkerState.HOVERED, {
                  w: 48,
                  h: 48,
                  count: meta.location.providerCount
                })
              : getMarkerIconForAws(AwsMarkerState.DEFAULT, {
                  w: 48,
                  h: 48,
                  count: meta.location.providerCount
                });

            markerElement.src = dataUrl;
            markerElement.style.zIndex = isHighlighted
              ? ZINDEXES.FOCUSED
              : ZINDEXES.UNFOCUSED;
          }
        }
      });
    },
    [providerLocationMap]
  );

  const highlightMarkers = useCallback(
    (providerId) => {
      if (!markersRef.current.length || !window) return;

      const firstMarker = markersRef.current[0]?.marker;
      const isGoogleMaps = firstMarker && firstMarker.setIcon && window.google;

      if (isGoogleMaps) {
        highlightGoogleMapsMarkers(providerId);
      } else {
        highlightAwsMapMarkers(providerId);
      }
    },
    [highlightGoogleMapsMarkers, highlightAwsMapMarkers]
  );

  useEffect(() => {
    const activeMarkers = markersRef.current.filter(
      ({ meta }) => meta.isClicked
    );
    if (activeMarkers.length > 0) {
      return;
    }
    highlightMarkers(hoveredProviderId);
  }, [hoveredProviderId, highlightMarkers]);

  const setMarkers = useCallback((markers) => {
    markersRef.current = markers || [];
  }, []);

  return {
    highlightMarkers,
    setMarkers
  };
};
